import { useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import type { FilterCondition, FilterOperator } from '@/types'

const FILTER_FIELDS = [
  { value: 'message', label: 'Error Message' },
  { value: 'level', label: 'Level' },
  { value: 'environment', label: 'Environment' },
  { value: 'release', label: 'Release' },
  { value: 'user_id', label: 'User ID' },
]

const FILTER_OPERATORS: { value: FilterOperator; label: string; requiresValue: boolean }[] = [
  { value: 'equals', label: 'equals', requiresValue: true },
  { value: 'not_equals', label: 'does not equal', requiresValue: true },
  { value: 'contains', label: 'contains', requiresValue: true },
  { value: 'not_contains', label: 'does not contain', requiresValue: true },
  { value: 'starts_with', label: 'starts with', requiresValue: true },
  { value: 'ends_with', label: 'ends with', requiresValue: true },
  { value: 'matches_regex', label: 'matches regex', requiresValue: true },
  { value: 'is_set', label: 'is set', requiresValue: false },
  { value: 'is_not_set', label: 'is not set', requiresValue: false },
]

interface FilterBuilderProps {
  conditions: FilterCondition[]
  onChange: (conditions: FilterCondition[]) => void
  disabled?: boolean
}

export function FilterBuilder({ conditions, onChange, disabled }: FilterBuilderProps) {
  const [customField, setCustomField] = useState('')
  const [showCustomField, setShowCustomField] = useState<number | null>(null)

  const addCondition = () => {
    onChange([...conditions, { field: 'message', operator: 'contains', value: '' }])
  }

  const updateCondition = (index: number, updates: Partial<FilterCondition>) => {
    const newConditions = [...conditions]
    newConditions[index] = { ...newConditions[index], ...updates }
    onChange(newConditions)
  }

  const removeCondition = (index: number) => {
    onChange(conditions.filter((_, i) => i !== index))
  }

  const handleFieldChange = (index: number, value: string) => {
    if (value === '__custom__') {
      setShowCustomField(index)
      setCustomField('')
    } else {
      updateCondition(index, { field: value })
      setShowCustomField(null)
    }
  }

  const handleCustomFieldConfirm = (index: number) => {
    if (customField.trim()) {
      const field = customField.startsWith('context.') ? customField : `context.${customField}`
      updateCondition(index, { field })
    }
    setShowCustomField(null)
    setCustomField('')
  }

  const getOperator = (op: FilterOperator) => {
    return FILTER_OPERATORS.find(o => o.value === op)
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500 mb-2">All conditions must match (AND logic)</div>

      {conditions.map((condition, index) => {
        const operator = getOperator(condition.operator)
        const isCustomField = !FILTER_FIELDS.find(f => f.value === condition.field)
        const showingCustomInput = showCustomField === index

        return (
          <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-3">
                {/* Row 1: Field and Operator */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Field selector */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Field</label>
                    {showingCustomInput ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={customField}
                          onChange={e => setCustomField(e.target.value)}
                          placeholder="context.path.to.field"
                          className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={disabled}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => handleCustomFieldConfirm(index)}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500"
                          disabled={disabled}
                        >
                          OK
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCustomField(null)}
                          className="p-2 text-slate-400 hover:text-slate-600"
                          disabled={disabled}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <select
                        value={isCustomField ? '__custom__' : condition.field}
                        onChange={e => handleFieldChange(index, e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={disabled}
                      >
                        {FILTER_FIELDS.map(field => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                        <option value="__custom__">Custom context field...</option>
                        {isCustomField && (
                          <option value={condition.field}>{condition.field}</option>
                        )}
                      </select>
                    )}
                  </div>

                  {/* Operator selector */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Operator
                    </label>
                    <select
                      value={condition.operator}
                      onChange={e =>
                        updateCondition(index, {
                          operator: e.target.value as FilterOperator,
                          value: getOperator(e.target.value as FilterOperator)?.requiresValue
                            ? condition.value
                            : null,
                        })
                      }
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={disabled}
                    >
                      {FILTER_OPERATORS.map(op => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2: Value input */}
                {operator?.requiresValue && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Value</label>
                    <input
                      type="text"
                      value={condition.value || ''}
                      onChange={e => updateCondition(index, { value: e.target.value })}
                      placeholder="Enter value to match..."
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>

              {/* Remove button */}
              <button
                type="button"
                onClick={() => removeCondition(index)}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors mt-5"
                disabled={disabled || conditions.length <= 1}
                title="Remove condition"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        )
      })}

      <button
        type="button"
        onClick={addCondition}
        className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
        disabled={disabled}
      >
        <Plus className="h-4 w-4" />
        Add condition
      </button>
    </div>
  )
}
